/**
 * Контроллер выгрузки: один сценарий «предупреждение → запуск → окно статуса»
 * на все три места вызова. Место вызова передаёт область действия и строки,
 * всё остальное — здесь, поэтому копий сценария не существует.
 */

import { useCallback, useRef, useState } from 'react'
import { useToastStore } from '../../stores/toastStore'
import { publishCardsAPI, publishRequestError } from './api'
import { usePublishJobsStore } from './publishJobsStore'
import { buildPublishPlan } from './publishScope'
import { PublishConfirmDialog } from './PublishConfirmDialog'
import { PublishStatusModal } from './PublishStatusModal'
import type {
  PublishCardTarget,
  PublishFlowMode,
  PublishMarketplace,
  PublishPlan,
  PublishScopeKind,
} from './types'

export interface PublishController {
  scope: PublishScopeKind
  mode: PublishFlowMode
  plan: PublishPlan | null
  pending: boolean
  statusJobId: string | null
  /** Открыть диалог подтверждения для этих карточек */
  requestPublish: (targets: PublishCardTarget[]) => void
  /** Запустить точный состав сразу; используется страницей предпросмотра. */
  publishNow: (targets: PublishCardTarget[]) => Promise<void>
  cancel: () => void
  confirm: () => Promise<void>
  closeStatus: () => void
}

export interface UsePublishCardsOptions {
  /** Диалог остаётся default; preview создаёт сохранённый набор и уходит на страницу. */
  mode?: PublishFlowMode
  /** SPA-навигация передаётся местом вызова, чтобы контроллер не зависел от router. */
  onNavigate?: (path: string) => void
  /** Вызывается после успешного запуска: обновить список, снять выбор */
  onStarted?: (jobId: string) => void
}

export function usePublishCards(
  scope: PublishScopeKind,
  options: UsePublishCardsOptions = {},
): PublishController {
  const { mode = 'dialog', onNavigate, onStarted } = options
  const showToast = useToastStore((state) => state.showToast)
  const registerJob = usePublishJobsStore((state) => state.registerJob)
  const openJob = usePublishJobsStore((state) => state.openJob)
  const closeJob = usePublishJobsStore((state) => state.closeJob)
  const statusJobId = usePublishJobsStore((state) => state.openJobId)

  const [plan, setPlan] = useState<PublishPlan | null>(null)
  const [pending, setPending] = useState(false)
  // State обновится только на следующем render. Ref закрывает окно между двумя
  // синхронными кликами, чтобы не создать два плана или две фоновые задачи.
  const pendingRef = useRef(false)

  const requestPublish = useCallback(
    (targets: PublishCardTarget[]) => {
      if (pendingRef.current) return
      const nextPlan = buildPublishPlan(targets, scope)
      if (mode === 'dialog') {
        setPlan(nextPlan)
        return
      }

      if (nextPlan.cardIds.length === 0) {
        showToast('Не удалось определить выбранные карточки для предпросмотра', 'error')
        return
      }

      pendingRef.current = true
      setPending(true)
      void publishCardsAPI.createPublishPlan(nextPlan.cardIds, scope === 'all' ? null : scope)
        .then((planId) => {
          onNavigate?.(`/marketplace/publish/${encodeURIComponent(planId)}`)
        })
        .catch((error) => {
          showToast(publishRequestError(error, 'Не удалось собрать набор карточек'), 'error')
        })
        .finally(() => {
          pendingRef.current = false
          setPending(false)
        })
    },
    [mode, onNavigate, scope, showToast],
  )

  const cancel = useCallback(() => {
    if (pending) return
    setPlan(null)
  }, [pending])

  const startPublish = useCallback(async (nextPlan: PublishPlan) => {
    if (nextPlan.cardCount === 0 || pendingRef.current) return
    pendingRef.current = true
    setPending(true)
    try {
      // jobId создаёт бэкенд: берём его из ответа, а не выдумываем на клиенте.
      const jobId = await publishCardsAPI.publishProductCards(nextPlan)
      registerJob({
        jobId,
        scope: nextPlan.scope,
        startedAt: new Date().toISOString(),
        articles: nextPlan.articles,
        targets: nextPlan.targets,
        results: [],
      })
      setPlan(null)
      openJob(jobId)
      showToast({
        type: 'success',
        title: 'Выгрузка запущена',
        message: 'Окно можно закрыть — операция продолжится в фоне.',
        jobId,
        duration: 8000,
      })
      onStarted?.(jobId)
    } catch (error) {
      showToast(publishRequestError(error, 'Не удалось запустить выгрузку'), 'error')
    } finally {
      pendingRef.current = false
      setPending(false)
    }
  }, [registerJob, openJob, showToast, onStarted])

  const publishNow = useCallback(
    async (targets: PublishCardTarget[]) => startPublish(buildPublishPlan(targets, scope)),
    [scope, startPublish],
  )

  const confirm = useCallback(async () => {
    if (!plan) return
    await startPublish(plan)
  }, [plan, startPublish])

  return {
    scope,
    mode,
    plan,
    pending,
    statusJobId,
    requestPublish,
    publishNow,
    cancel,
    confirm,
    closeStatus: closeJob,
  }
}

/** Оба окна сценария. Ставится один раз рядом с таблицей места вызова. */
export function PublishCardsOverlays({ controller }: { controller: PublishController }) {
  return (
    <>
      {controller.mode === 'dialog' && (
        <PublishConfirmDialog
          isOpen={controller.plan !== null}
          plan={controller.plan}
          pending={controller.pending}
          onClose={controller.cancel}
          onConfirm={() => void controller.confirm()}
        />
      )}
      <PublishStatusModal
        jobId={controller.statusJobId}
        isOpen={controller.statusJobId !== null}
        onClose={controller.closeStatus}
      />
    </>
  )
}
